// use-sonos-favourites.jsx — Sonos Favourites discovery via HA's
// media_player/browse_media WebSocket message.
//
// Per BRIEF-music-card.md §10 "Sonos Favourites": the card never owns
// the favourites list — Sonos owns it (set up in the Sonos app). This
// hook does a one-shot browse: root → find the "Favorites" child →
// drill into it → returns the flat list of household favourites.
//
// Cached at module scope (single set of favourites per panel session).
// Originally tried a WeakMap keyed by hass for cache-invalidation on
// reconnect, but `hass` gets a new object reference on every HA state
// event, so cache.has(hass) was always false — the cache never hit,
// and we re-browsed on every render. Module-scope cache + dedupe via
// an inflight promise sidesteps that entirely.
//
// Browse entity: any Sonos works. We pick office_turntable because
// it's always present (Sonos Port wired to vinyl) and never busy with
// TV (unlike the Arc, which is a TV soundbar).
//
// Folder discovery uses the same regex as direction-a.jsx's
// findFavouritesChild — matches "Favorites" / "Favourites" / similar.

import * as React from 'react';

const { useState, useEffect, useRef } = React;

const FAVOURITES_ENTITY = 'media_player.office_turntable';

// Module-scope cache + inflight dedupe with a short TTL. Cache survives
// within a single MusicShell session (S0 remounts hit it for free, no
// duplicate browse_media calls). After TTL expires, the next consumer
// triggers a fresh fetch — so favourites added/removed in the Sonos
// app show up the next time the user opens MusicShell after the TTL
// window. 15s is a balance between freshness and not hammering HA on
// rapid open/close cycles.
const CACHE_TTL_MS = 15 * 1000;
let cachedFavs = null;       // array | null
let cachedError = null;      // Error | null
let cachedAt = 0;            // ms — Date.now() of last successful fetch
let inflight = null;         // Promise | null

function cacheIsFresh() {
  return cachedFavs !== null && (Date.now() - cachedAt) < CACHE_TTL_MS;
}

function isFavouritesNode(child) {
  if (!child) return false;
  if (/favo(u)?rit/i.test(child.title || '')) return true;
  if ((child.media_content_id || '').toLowerCase().includes('favorite')) return true;
  return false;
}

// Spotify "no artwork" placeholder URLs that Sonos returns in
// browse_media for playlists/items without custom cover art. Both 404
// — the S3 bucket is stale and the pl.scdn.co default route returns
// nothing. When a favourite's thumbnail matches one of these AND the
// favourite is Spotify-backed (its content_id contains a Spotify URI),
// enrichSpotifyArtwork replaces the dead URL with the real auto-
// generated cover from Spotify's Web API. Source of dead-URL truth
// must stay in sync with ContentTile's isDeadPlaceholder check.
const DEAD_SPOTIFY_PLACEHOLDERS = [
  'spotify-static-resources.s3.amazonaws.com/img/playlist_legacy',
  'pl.scdn.co/images/pl/default/',
];
function isDeadPlaceholder(url) {
  if (!url) return false;
  return DEAD_SPOTIFY_PLACEHOLDERS.some((p) => url.includes(p));
}

// Extract a Spotify URI (kind + id) from a Sonos media_content_id.
// Sonos wraps Spotify content in URLs like
//   x-rincon-cpcontainer:1006206cspotify%3aplaylist%3a37i6dQ...?sid=12&...
// where the spotify URI is URL-encoded inside. Decode then regex.
function spotifyIdFrom(contentId) {
  if (!contentId) return null;
  let decoded;
  try { decoded = decodeURIComponent(contentId); }
  catch { decoded = contentId; }
  const m = decoded.match(/spotify:(playlist|album|track|artist):([a-zA-Z0-9]+)/);
  return m ? { kind: m[1], id: m[2] } : null;
}

// Spotify Web API → real cover URL. Plays nicely with the existing
// token bridge — getToken (passed in from S0 → useSonosFavourites) is
// the same memoised callback used by useSpotifyApi for search/browse,
// so this enrichment doesn't widen the auth surface.
async function enrichSpotifyArtwork(favs, getToken) {
  // Sonos sometimes references favourites by an opaque slot ID like
  // `FV:2/15` rather than a content URI — for those we have no
  // Spotify playlist/album ID to look up. They fall through to the
  // CoverArt gradient (graceful, identified by title). Filter keeps
  // only the dead-URL favs that DO expose a Spotify URI.
  const dead = favs
    .map((f, i) => ({ f, i, spotify: spotifyIdFrom(f.id) }))
    .filter(({ f, spotify }) => isDeadPlaceholder(f.thumbnail) && spotify);
  if (dead.length === 0 || !getToken) return favs;
  let token;
  try { token = await getToken(); }
  catch (e) {
    console.warn('[music/favs] enrichment skipped — getToken failed', e);
    return favs;
  }
  const enriched = favs.slice();
  await Promise.all(dead.map(async ({ f, i, spotify }) => {
    const endpoint = spotify.kind === 'playlist' ? 'playlists'
                   : spotify.kind === 'album'    ? 'albums'
                   : spotify.kind === 'artist'   ? 'artists'
                   : spotify.kind === 'track'    ? 'tracks'
                   : null;
    if (!endpoint) return;
    try {
      const res = await fetch(`https://api.spotify.com/v1/${endpoint}/${spotify.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;  // 404/401 → fall through to CoverArt
      const data = await res.json();
      // Tracks expose images via album.images; everything else direct.
      const images = data.images || data.album?.images || [];
      const realUrl = images[0]?.url || null;
      if (realUrl) enriched[i] = { ...f, thumbnail: realUrl };
    } catch (e) {
      // Network / parse failure — silent fallback to CoverArt.
    }
  }));
  return enriched;
}

async function loadFavourites(hass, getToken) {
  if (cacheIsFresh()) return cachedFavs;
  // Note: deliberately do NOT throw a cached error here — transient
  // failures (HA mid-restart, WS hiccup) shouldn't lock out favourites
  // for the rest of the session. Each call gets a fresh attempt; the
  // most recent error is the one the caller sees.
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const root = await hass.callWS({
        type: 'media_player/browse_media',
        entity_id: FAVOURITES_ENTITY,
      });
      const favsNode = (root?.children || []).find(isFavouritesNode);
      if (!favsNode) {
        cachedFavs = [];
        cachedAt = Date.now();
        return cachedFavs;
      }
      const favs = await hass.callWS({
        type: 'media_player/browse_media',
        entity_id: FAVOURITES_ENTITY,
        media_content_id: favsNode.media_content_id,
        media_content_type: favsNode.media_content_type,
      });

      // Sonos's "Favorites" node typically contains *category folders*
      // (Albums, Playlists, Stations, etc.) rather than leaf items.
      // Drill into each expandable child in parallel + flatten the
      // playable leaves into one combined list. Anything already a
      // leaf (can_play) is kept as-is. Sub-folder errors get logged
      // and skipped rather than failing the whole fetch.
      const topChildren = favs?.children || [];
      const subFetches = topChildren.map((c) => {
        if (c.can_play) return Promise.resolve([c]);
        if (c.can_expand) {
          return hass.callWS({
            type: 'media_player/browse_media',
            entity_id: FAVOURITES_ENTITY,
            media_content_id: c.media_content_id,
            media_content_type: c.media_content_type,
          })
            .then((sub) => (sub?.children || []).filter((i) => i.can_play))
            .catch((e) => {
              console.warn('[music/favs] sub-folder fetch failed', c.title, e);
              return [];
            });
        }
        return Promise.resolve([]);
      });
      const leaves = (await Promise.all(subFetches)).flat();
      const baseFavs = leaves.map((c) => ({
        id: c.media_content_id,
        title: c.title || '(untitled)',
        thumbnail: c.thumbnail || null,
        contentType: c.media_content_type,
      }));
      // Replace Spotify dead-placeholder thumbnails with real Web API
      // artwork. If getToken isn't provided (or fails), favourites
      // still load — they just fall back to CoverArt gradients for
      // the dead-URL ones, same as before.
      cachedFavs = await enrichSpotifyArtwork(baseFavs, getToken);
      cachedAt = Date.now();
      return cachedFavs;
    } catch (e) {
      // Don't persist the error — next call retries fresh.
      console.warn('[music/favs] load failed (will retry on next call)', e);
      throw e;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export function useSonosFavourites(hass, getToken) {
  // Stash hass + getToken in refs so the load effect doesn't re-fire
  // on every HA state-change render. Same identity-churn pattern as
  // use-spotify-token.
  const hassRef = useRef(hass);
  useEffect(() => { hassRef.current = hass; }, [hass]);
  const getTokenRef = useRef(getToken);
  useEffect(() => { getTokenRef.current = getToken; }, [getToken]);

  const [favourites, setFavourites] = useState(cachedFavs);
  const [error, setError] = useState(cachedError);

  const hasHass = !!hass;
  useEffect(() => {
    if (!hasHass) return;
    // Always try loadFavourites — its internal TTL check returns the
    // cached array if fresh, otherwise triggers a fresh fetch. The
    // cache survives across S0 remounts within a single MusicShell
    // session; it just refreshes when MusicShell reopens after a
    // 15-second gap.
    let cancelled = false;
    loadFavourites(hassRef.current, getTokenRef.current)
      .then((items) => { if (!cancelled) setFavourites(items); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [hasHass]);

  return {
    favourites: favourites || [],
    loading: favourites === null && !error,
    error,
  };
}
